/*
 * The five /start steps (design W1 with the 10B states and pick 4A). Each step
 * owns its own field, busy and problem state; the page (./index.jsx) owns the
 * flow — the email, the signup ticket, the phone — and the outcomes every step
 * shares (offer off, lapsed, ticket expired).
 *
 * Errors sit under their field; a page-level problem sits above the form as
 * one calm box. Nothing here is a toast.
 */
import React, { useEffect, useRef, useState } from "react";
import { SignalSlashIcon, EnvelopeIcon, ClockIcon } from "@heroicons/react/24/solid";
import { ft, fw, fc, Btn, Msg, Alert, Spinner } from "components/FreeEvent";
import CodeField from "components/FreeEvent/CodeField";
import PhoneField, { toE164, DEFAULT_COUNTRY } from "components/FreeEvent/PhoneField";
import { formatCountdown, formatEventDateTime } from "utils/eventTime";
import { trackStartError, trackPhoneWaitShown } from "utils/freeEventAnalytics";
import useCountdown, { deadline } from "./useCountdown";
import { classify, minutesFrom, typoSuggestion, isEmail, domainOf } from "./errors";

const Offline = () => <Alert kind="err" icon={SignalSlashIcon} title={ft("em.offline")} />;

// The "too many tries" box: words from the server's retry_after; the action
// stays off until it passes.
const RateAlert = ({ copyKey, retryAfter }) => {
  const minutes = minutesFrom(retryAfter);
  return <Alert kind="warn" icon={ClockIcon} title={fc(copyKey, minutes, { minutes })} />;
};

const useRate = () => {
  const [until, setUntil] = useState(null);
  const left = useCountdown(until);
  return { limited: left !== null && left > 0, start: (s) => setUntil(deadline(s || 60)), clear: () => setUntil(null) };
};

/* ───────────── 1 · Email ───────────── */

export const EmailStep = ({ email, onEmail, notice, send, onShared }) => {
  const [problem, setProblem] = useState(null);
  const [busy, setBusy] = useState(false);
  const rate = useRate();
  const suggestion = typoSuggestion(email);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || rate.limited) return;
    const value = email.trim();
    if (!isEmail(value)) {
      setProblem({ kind: "invalid" });
      trackStartError("email", "invalid_format");
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await send(value);
    } catch (err) {
      const c = classify(err);
      trackStartError("email", c.code);
      if (onShared(c)) return;
      if (c.kind === "rate") rate.start(c.retryAfter);
      setProblem(c);
    } finally {
      setBusy(false);
    }
  };

  const fieldErr =
    problem &&
    ((problem.kind === "invalid" && ft("em.invalid")) ||
      (problem.kind === "rejected" && problem.code === "invalid" && ft("em.invalid")) ||
      (problem.kind === "rejected" && problem.code === "undeliverable" && ft("em.nomx", { domain: domainOf(email) })));
  const disposable = problem && problem.kind === "rejected" && problem.code === "disposable";

  return (
    <form onSubmit={submit} noValidate>
      {notice === "restart" && !problem && <Alert kind="info" title={ft("em.restart")} />}
      {disposable && <Alert kind="err" title={ft("em.disp.b")}>{fw("em.disp")}</Alert>}
      {problem && problem.kind === "rate" && rate.limited && <RateAlert copyKey="em.rate" retryAfter={problem.retryAfter} />}
      {problem && problem.kind === "unavailable" && <Alert kind="err" title={ft("em.send")} />}
      {problem && problem.kind === "offline" && <Offline />}
      <div className="fld">
        <label htmlFor="fs-email">{ft("em.label")}</label>
        <input
          id="fs-email"
          className={`inp${fieldErr || disposable ? " err" : ""}`}
          type="email"
          name="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          placeholder={ft("em.ph")}
          value={email}
          onChange={(e) => {
            onEmail(e.target.value);
            if (problem && problem.kind !== "rate") setProblem(null);
          }}
          disabled={busy}
          aria-invalid={Boolean(fieldErr || disposable) || undefined}
          aria-describedby={fieldErr ? "fs-email-err" : suggestion ? "fs-email-hint" : undefined}
        />
        {fieldErr && <Msg id="fs-email-err">{fieldErr}</Msg>}
        {!fieldErr && suggestion && (
          <Msg kind="info" id="fs-email-hint">
            <TypoHint suggestion={suggestion} onPick={() => onEmail(suggestion)} />
          </Msg>
        )}
      </div>
      <Btn type="submit" className="block" busy={busy} busyLabel={ft("em.busy")} disabled={rate.limited}>
        {ft("em.btn")}
      </Btn>
    </form>
  );
};


// "Did you mean name@gmail.com?" with the address as the button that applies it.
const TypoHint = ({ suggestion, onPick }) => {
  const line = ft("em.typo", { suggested_email: "\u0000" });
  const [before, after] = line.split("\u0000");
  return (
    <>
      {before}
      <button type="button" className="linkish" onClick={onPick}>
        {suggestion}
      </button>
      {after}
    </>
  );
};

/* ───────────── 2 and 4 · a code (email or phone) ───────────── */

const CODE_COPY = {
  email: { wait: "ec.wait" },
  phone: { wait: "pc.wait" },
  call: { wait: "pc.call.wait" },
};

export const CodeStep = ({ kind, stepName, resendAfter, verify, resend, onShared }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [sent, setSent] = useState(false);
  const [resendUntil, setResendUntil] = useState(() => deadline(resendAfter));
  const [resending, setResending] = useState(false);
  const left = useCountdown(resendUntil);
  const rate = useRate();
  const copy = CODE_COPY[kind];
  // Verify and resend never overlap: a phone-code send clears the phone proof
  // on the server, so a resend landing after a good verify would strand the
  // name step on phone_required. The ref is read synchronously, so a double
  // tap inside one render can't slip past the disabled state.
  const inFlight = useRef(null);
  const verified = useRef(false);

  const submit = async (value) => {
    const v = value || code;
    if (inFlight.current || verified.current || rate.limited || v.length !== 6) return;
    inFlight.current = "verify";
    setBusy(true);
    setProblem(null);
    setSent(false);
    try {
      await verify(v);
      verified.current = true;
    } catch (err) {
      const c = classify(err);
      trackStartError(stepName, c.code);
      if (onShared(c)) return;
      if (c.kind === "rate") rate.start(c.retryAfter);
      setProblem(c);
    } finally {
      inFlight.current = null;
      setBusy(false);
    }
  };

  const doResend = async () => {
    if (inFlight.current || verified.current || rate.limited) return;
    inFlight.current = "resend";
    setResending(true);
    setProblem(null);
    try {
      const res = await resend();
      // A verify can't start while this is in flight, but never let a late
      // resend touch a step that has already been verified.
      if (verified.current) return;
      setCode("");
      setSent(true);
      setResendUntil(deadline(res && res.resend_after));
    } catch (err) {
      const c = classify(err);
      trackStartError(stepName, c.code);
      if (onShared(c)) return;
      if (c.kind === "rate") rate.start(c.retryAfter);
      setProblem(c);
    } finally {
      inFlight.current = null;
      setResending(false);
    }
  };

  const expired = problem && problem.kind === "rejected" && problem.code === "code_expired";
  const wrong = problem && problem.kind === "rejected" && !expired;
  const dead = problem && problem.kind === "attempts";
  const resendLink = (
    <button type="button" className="linkish solo" onClick={doResend} disabled={busy || resending || rate.limited}>
      {ft("ec.resend")}
    </button>
  );
  const waitLine =
    left > 0 ? (
      <p className="hint" id="fs-code-wait">{ft(copy.wait, { countdown: formatCountdown(left) })}</p>
    ) : (
      <p className="hint" id="fs-code-wait">{resendLink}</p>
    );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      noValidate
    >
      {dead && (
        <Alert kind="err" title={ft("code.many")}>
          <p>{resendLink}</p>
        </Alert>
      )}
      {problem && problem.kind === "rate" && rate.limited && <RateAlert copyKey="em.rate" retryAfter={problem.retryAfter} />}
      {problem && problem.kind === "unavailable" && <Alert kind="err" title={ft("code.503")} />}
      {problem && problem.kind === "offline" && <Offline />}
      <CodeField
        id="fs-code"
        label={ft("ec.label")}
        value={code}
        onChange={(v) => {
          setCode(v);
          if (problem && problem.kind === "rejected") setProblem(null);
        }}
        onComplete={submit}
        invalid={Boolean(wrong || expired)}
        disabled={busy || resending || dead || rate.limited}
        describedBy={wrong || expired ? "fs-code-err" : dead ? undefined : "fs-code-wait"}
        autoFocus
      />
      <div className="fld" style={{ marginTop: -8 }}>
        {wrong && <Msg id="fs-code-err">{ft("code.wrong")}</Msg>}
        {expired && <Msg id="fs-code-err">{ft("code.expired")}</Msg>}
        {sent && !problem && <Msg kind="ok">{ft("ec.sent")}</Msg>}
        {!dead && (expired ? <p className="hint">{resendLink}</p> : waitLine)}
      </div>
      <Btn type="submit" className="block" busy={busy} busyLabel={ft("code.busy")} disabled={resending || dead || rate.limited || code.length !== 6}>
        {ft("code.btn")}
      </Btn>
    </form>
  );
};

/* ───────────── 3 · Phone (10B, pick 4A) ───────────── */

const FALLBACK_RETRY_S = 30; // only when the 503 carries no retry_after
const DAY_MS = 24 * 60 * 60 * 1000;
// No event exists yet at signup, so the resume time reads in the viewer's zone.
const viewerZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const PhoneStep = ({ phone, onPhone, country, onCountry, email, sendCode, sendLater, onShared }) => {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [waitUntil, setWaitUntil] = useState(null);
  const [later, setLater] = useState(false);
  const [laterBusy, setLaterBusy] = useState(false);
  const left = useCountdown(waitUntil);
  const rate = useRate();
  const down = problem && problem.kind === "down";
  const autoRetried = useRef(false);

  const submit = async () => {
    if (busy || rate.limited) return;
    const e164 = toE164(phone, country);
    if (!e164) {
      setProblem({ kind: "invalid" });
      trackStartError("phone", "invalid_format");
      return;
    }
    setBusy(true);
    try {
      await sendCode(e164);
    } catch (err) {
      const c = classify(err);
      trackStartError("phone", c.code);
      if (onShared(c)) return;
      if (c.kind === "unavailable" && c.code === "phone_check_unavailable") {
        if (!down) trackPhoneWaitShown();
        autoRetried.current = false;
        setWaitUntil(deadline(c.retryAfter || FALLBACK_RETRY_S));
        setProblem({ kind: "down" });
        return;
      }
      if (c.kind === "rate") rate.start(c.retryAfter);
      setProblem(c);
    } finally {
      setBusy(false);
    }
  };

  // 4A: the page retries by itself when the countdown runs out.
  useEffect(() => {
    // The deadline itself is checked, so a fresh 503 never retries on the last
    // countdown's zero.
    if (down && waitUntil && Date.now() >= waitUntil && !busy && !autoRetried.current) {
      autoRetried.current = true;
      submit();
    }
  });

  const askLater = async () => {
    if (laterBusy) return;
    setLaterBusy(true);
    try {
      const res = await sendLater();
      // The link lasts 24 hours (design Req 6); rails names the exact time.
      setLater({ until: (res && res.resume_expires_at) || new Date(Date.now() + DAY_MS).toISOString() });
    } catch (err) {
      const c = classify(err);
      trackStartError("phone", c.code);
      if (onShared(c)) return;
      setProblem(c.kind === "offline" ? c : { kind: "down" });
    } finally {
      setLaterBusy(false);
    }
  };

  const invalid = problem && (problem.kind === "invalid" || (problem.kind === "rejected" && problem.code !== "phone_virtual"));
  const virtual = problem && problem.kind === "rejected" && problem.code === "phone_virtual";
  const holding = down || later;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      noValidate
    >
      {virtual && <Alert kind="err" title={ft("ph.virt.b")}>{fw("ph.virt")}</Alert>}
      {down && !later && (
        <Alert kind="warn" icon={ClockIcon} title={ft("ph.down.b")}>
          {fw("ph.down")}
          <p className="msg info" style={{ marginTop: 12 }}>
            <Spinner />
            <span>{ft("ph.down.t", { countdown: formatCountdown(left || 0) })}</span>
          </p>
        </Alert>
      )}
      {later && (
        <Alert kind="info" icon={EnvelopeIcon} title={ft("ph.down.b")}>
          {fw("ph.down.sent", { email, resume_expires_at: formatEventDateTime(later.until, viewerZone()) })}
        </Alert>
      )}
      {problem && problem.kind === "rate" && rate.limited && <RateAlert copyKey="ph.rate" retryAfter={problem.retryAfter} />}
      {problem && problem.kind === "unavailable" && <Alert kind="err" title={ft("em.send")} />}
      {problem && problem.kind === "offline" && <Offline />}
      <PhoneField
        id="fs-phone"
        label={ft("ph.label")}
        countryLabel={ft("ph.country")}
        value={phone}
        country={country}
        onChange={(v) => {
          onPhone(v);
          if (problem && !holding && problem.kind !== "rate") setProblem(null);
        }}
        onCountryChange={onCountry}
        invalid={Boolean(invalid || virtual)}
        disabled={busy || holding}
        describedBy={invalid ? "fs-phone-err" : undefined}
        autoFocus
      />
      {invalid && (
        <div className="fld" style={{ marginTop: -8 }}>
          <Msg id="fs-phone-err">{ft(country === DEFAULT_COUNTRY ? "ph.invalid" : "ph.invalid.intl")}</Msg>
        </div>
      )}
      {down && !later ? (
        <div className="later-row">
          <Btn className="ghost" busy={busy} onClick={submit}>
            {ft("ph.down.now")}
          </Btn>
          <button type="button" className="linkish solo" onClick={askLater} disabled={laterBusy}>
            {ft("ph.down.later")}
          </button>
        </div>
      ) : later ? (
        <Btn className="ghost" busy={busy} onClick={submit}>
          {ft("ph.down.now")}
        </Btn>
      ) : (
        <>
          <Btn type="submit" className="block" busy={busy} busyLabel={ft("ph.busy")} disabled={rate.limited}>
            {ft("ph.btn")}
          </Btn>
          <p className="st-foot">{fw("ph.foot")}</p>
        </>
      )}
    </form>
  );
};

/* ───────────── 5 · Name ───────────── */

const TERMS_HREF = "/terms";
const PRIVACY_HREF = "https://www.movebooth.com/privacy";

// The terms line with "Terms" and "Privacy Policy" as links, whatever the
// words around them (the line waits for counsel).
const TermsLine = () => {
  const line = ft("nm.terms");
  const pieces = line.split(/(Terms|Privacy Policy)/);
  return (
    <p className="st-foot">
      {pieces.map((piece, i) =>
        piece === "Terms" ? (
          <a key={i} href={TERMS_HREF}>{piece}</a>
        ) : piece === "Privacy Policy" ? (
          <a key={i} href={PRIVACY_HREF} target="_blank" rel="noreferrer noopener">{piece}</a>
        ) : (
          <React.Fragment key={i}>{piece}</React.Fragment>
        )
      )}
    </p>
  );
};

export const NameStep = ({ firstName, onFirstName, company, onCompany, create, onShared }) => {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!firstName.trim()) {
      setProblem({ kind: "required" });
      trackStartError("name", "first_name_missing");
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await create();
    } catch (err) {
      const c = classify(err);
      trackStartError("name", c.code);
      if (onShared(c)) return;
      setProblem(c);
    } finally {
      setBusy(false);
    }
  };

  const required = problem && problem.kind === "required";
  return (
    <form onSubmit={submit} noValidate>
      {problem && problem.kind === "offline" && <Offline />}
      {problem && (problem.kind === "unavailable" || problem.kind === "rejected" || problem.kind === "rate") && (
        <Alert kind="err" title={ft("nm.503")} />
      )}
      <div className="fld">
        <label htmlFor="fs-first">{ft("nm.first")}</label>
        <input
          id="fs-first"
          className={`inp${required ? " err" : ""}`}
          name="given-name"
          autoComplete="given-name"
          value={firstName}
          onChange={(e) => {
            onFirstName(e.target.value);
            if (required) setProblem(null);
          }}
          disabled={busy}
          aria-invalid={required || undefined}
          aria-describedby={required ? "fs-first-err" : undefined}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        {required && <Msg id="fs-first-err">{ft("nm.req")}</Msg>}
      </div>
      <div className="fld">
        <label htmlFor="fs-company">
          {ft("nm.co")} <span className="opt">{ft("nm.opt")}</span>
        </label>
        <input
          id="fs-company"
          className="inp"
          name="organization"
          autoComplete="organization"
          value={company}
          onChange={(e) => onCompany(e.target.value)}
          disabled={busy}
        />
      </div>
      <Btn type="submit" className="block" busy={busy} busyLabel={ft("nm.busy")}>
        {ft("nm.btn")}
      </Btn>
      <TermsLine />
    </form>
  );
};
